import React, { useState, useCallback, useEffect } from 'react';
import get from 'lodash.get';
import { useSelector, useDispatch } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import { emptyArray } from 'ui-components/default-props/empty';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { createEmptyItem } from '../../../services/empty-order-creator';
import Global from '../../../store/global-selectors';
import { orderDetailsSaveOrderItemsRequest } from '../../../store/orders/order-details/orders.details.actions';
import { orderPropertyToValidatorMap } from '../../../store/orders/orders.helpers';

import EditableOrderItems from './index';

const priceCheckItems = (workingOrderItems) => {
  // check each item for empty price
  const priceCheckedItems = workingOrderItems.map((item) => {
    if (item.unitPrice && item.unitPrice.value === '') {
      return {
        ...item,
        unitPrice: {
          ...item.unitPrice,
          value: 0,
        },
      };
    }
    return item;
  });
  return priceCheckedItems;
};

const adaptQuantityFromWorkingItems = (workingOrderItems) => {
  // we use `quantity` in practice, but need to adapt to quantityOrdered until we standardize backend/frontend
  const quantityAdaptedItems = workingOrderItems.map((item) => {
    return {
      ...item,
      quantityOrdered: item.quantity || 0,
    };
  });

  return quantityAdaptedItems;
};

/**
 * State manager component for editable order items
 * @param {object} props - Component props
 * @param {(items: object[]) => void} [props.onChange] - Callback when items change
 * @param {(hasError: boolean) => void} [props.onValidationChange] - Callback when validation state changes
 * @param {boolean} [props.shouldShowValidationErrors] - Whether to show validation errors
 * @param {object} [props.order] - Order object (used to get items from Redux if not provided directly)
 * @param {() => void} [props.onCancel] - Callback when cancelled
 * @param {boolean} [props.shouldRenderSaveAndCancel] - Whether to render save and cancel buttons
 * @param {boolean} [props.shouldInitializeNewItem] - Whether to initialize with new item
 * @returns {JSX.Element} The rendered EditableOrderItems component
 */
const EditableOrderItemsStateManager = ({
  onChange = noop,
  onValidationChange = noop,
  shouldShowValidationErrors = false,
  order,
  onCancel = noop,
  shouldRenderSaveAndCancel = false,
  shouldInitializeNewItem = false,
}) => {
  const dispatch = useDispatch();

  const salesOrderId = useSelector(Global.selectOrderDetailsSalesOrderId);
  const defaultCurrency = useSelector(Global.selectSellerDefaultCurrency);
  const salesOrderFromState = useSelector((state) => Global.selectEntitiesSalesOrderById(state, { salesOrderId }));

  const salesOrder = order || salesOrderFromState;
  const orderItems = get(salesOrder, 'items', emptyArray);

  const generateNewOrderItem = useCallback(() => createEmptyItem(defaultCurrency), [defaultCurrency]);
  const saveOrderItems = useCallback((items) => dispatch(orderDetailsSaveOrderItemsRequest(items)), [dispatch]);
  const initializeItems = useCallback(() => {
    const items = orderItems.length === 0 && shouldInitializeNewItem ? [generateNewOrderItem()] : [...orderItems];
    return items;
  }, [orderItems, generateNewOrderItem, shouldInitializeNewItem]);

  const [workingOrderItems, setWorkingOrderItems] = useState(() => initializeItems());
  const [focusedItem, setFocusedItem] = useState({ index: -1, field: '' });
  const [isSavingItemEdits, setIsSavingItemEdits] = useState(false);
  const [hasError, setHasError] = useState(false);

  const itemsHaveError = useCallback(() => {
    return orderPropertyToValidatorMap.items({ items: workingOrderItems });
  }, [workingOrderItems]);

  useEffect(() => {
    if (shouldShowValidationErrors) {
      const hasErrorValue = itemsHaveError();
      setHasError(hasErrorValue);
      onValidationChange(hasErrorValue);
    }
  }, [shouldShowValidationErrors, onValidationChange, itemsHaveError]);

  useOnComponentMount(() => {
    const items = initializeItems();
    setWorkingOrderItems(items);
    onChange(items);
  });

  const onItemSelected = useCallback(
    (item, index) => {
      const indexOfSelectedProduct = focusedItem.index;
      const selectedProductValue = workingOrderItems?.[indexOfSelectedProduct]?.unitPrice?.value || 0;
      const hasSalesOrderItemId = !!workingOrderItems?.[0]?.salesOrderItemId;
      const priceInfo = hasSalesOrderItemId ? selectedProductValue : 0;
      const items = [
        ...workingOrderItems.slice(0, index),
        {
          sku: item.sku,
          name: item.name,
          quantityOrdered: 1,
          quantity: 1,
          unitPrice: {
            value: priceInfo,
            code: item.unitPrice.code,
          },
        },
        ...workingOrderItems.slice(index + 1),
      ];
      const priceCheckedItems = priceCheckItems(items);
      const quantityAdaptedItems = adaptQuantityFromWorkingItems(priceCheckedItems);
      setWorkingOrderItems(quantityAdaptedItems);
      onChange(quantityAdaptedItems);
    },
    [workingOrderItems, focusedItem.index, onChange],
  );

  const onOrderItemChange = useCallback(
    (value, field, index) => {
      const regexPattern = /^$|\d+$/;
      if ((field !== 'quantityOrdered' && field !== 'quantity') || regexPattern.test(value)) {
        const items = [
          ...workingOrderItems.slice(0, index),
          { ...workingOrderItems[index], [field]: value },
          ...workingOrderItems.slice(index + 1),
        ];
        const priceCheckedItems = priceCheckItems(items);
        const quantityAdaptedItems = adaptQuantityFromWorkingItems(priceCheckedItems);
        setWorkingOrderItems(quantityAdaptedItems);
        onChange(quantityAdaptedItems);
        setIsSavingItemEdits(false);
      }
    },
    [workingOrderItems, onChange],
  );

  const handleChange = useCallback(
    (newValue, field, index) => {
      const { index: focusedIndex, field: focusedField } = focusedItem;
      if (focusedIndex !== index || focusedField !== field) {
        setFocusedItem({ index, field });
      }
      onOrderItemChange(newValue, field, index);
      setIsSavingItemEdits(false);
      setHasError(false);
      onValidationChange(false);
    },
    [focusedItem, onOrderItemChange, onValidationChange],
  );

  const onClickOffSearchResults = useCallback(() => {
    setFocusedItem({ index: -1, field: '' });
  }, []);

  const saveEdit = useCallback(() => {
    if (workingOrderItems.some((item) => !item.name || !item.quantity) || itemsHaveError()) {
      setHasError(true);
      onValidationChange(true);
      setIsSavingItemEdits(true);
      return;
    }

    const priceCheckedItems = priceCheckItems(workingOrderItems);
    const quantityAdaptedItems = adaptQuantityFromWorkingItems(priceCheckedItems);
    setWorkingOrderItems(quantityAdaptedItems);
    onChange(quantityAdaptedItems);
    setIsSavingItemEdits(true);
    saveOrderItems(quantityAdaptedItems);
  }, [workingOrderItems, itemsHaveError, onValidationChange, onChange, saveOrderItems]);

  const generateNewOrderItemHandler = useCallback(() => {
    const items = [...workingOrderItems, generateNewOrderItem()];
    const priceCheckedItems = priceCheckItems(items);
    const quantityAdaptedItems = adaptQuantityFromWorkingItems(priceCheckedItems);
    setWorkingOrderItems(quantityAdaptedItems);
    onChange(quantityAdaptedItems);
  }, [workingOrderItems, generateNewOrderItem, onChange]);

  const deleteOrderItem = useCallback(
    (index) => {
      const items = [...workingOrderItems.slice(0, index), ...workingOrderItems.slice(index + 1)];
      const priceCheckedItems = priceCheckItems(items);
      const quantityAdaptedItems = adaptQuantityFromWorkingItems(priceCheckedItems);
      setWorkingOrderItems(quantityAdaptedItems);
      onChange(quantityAdaptedItems);
      setIsSavingItemEdits(false);
    },
    [workingOrderItems, onChange],
  );

  return (
    <EditableOrderItems
      focusedField={focusedItem.field}
      focusedIndex={focusedItem.index}
      hasError={hasError}
      isSavingItemEdits={isSavingItemEdits}
      onAddItem={generateNewOrderItemHandler}
      onCancel={onCancel}
      onChange={handleChange}
      onClickOffSearchResults={onClickOffSearchResults}
      onDelete={deleteOrderItem}
      onSave={saveEdit}
      onSearchResultSelected={onItemSelected}
      orderItems={workingOrderItems}
      setIsSavingItemEdits={setIsSavingItemEdits}
      shouldRenderSaveAndCancel={shouldRenderSaveAndCancel}
    />
  );
};

EditableOrderItemsStateManager.displayName = 'EditableOrderItemsStateManager';

export default EditableOrderItemsStateManager;
